import { PINNED_CORNER } from './pinnedFirstColumn';

/**
 * One look for every data table in the CRM, taken from the Users table, the reference for list screens
 * (design-system Rev 41, §6 data tables). Each table builds from the shadcn Table parts (AD #39) and gives
 * them these classes, so a header, a cell or a row's height cannot drift from one table to the next.
 *
 * - **The table** collapses no borders (`border-separate`), so a sticky header and a pinned first column
 *   keep their own edges; each cell draws its own rule, because a `<tr>`'s border is not painted then.
 * - **The header row** is unshaded and in sentence case, at the body cells' size and colour, set apart by
 *   semibold weight and its bottom border.
 * - **A body cell** is secondary type in the body colour, wrapping only between words, with the row's
 *   rule and hover fill. One row height for skeleton and populated rows.
 * - **The ⋯ menu's cell** is as narrow as its trigger, at the end of the row (Rev 70: row actions live in
 *   the ⋯ menu).
 *
 * The classes are written only here, so `src/ui/styles/shadcn.css` names this file as a Tailwind
 * `@source`: without it the build generates none of them.
 */

/** The `<table>`: separate borders, so sticky cells keep their rules. Add `PIN_GROUP` when it pins. */
export const DATA_TABLE = 'border-separate border-spacing-0';

/** The scroll region around the table: it scrolls both ways inside its card, with a thin scrollbar. */
export const DATA_TABLE_REGION =
    'flex-[0_1_auto] min-h-[calc(var(--et-space-8)*5)] overflow-auto [scrollbar-width:thin] [scrollbar-gutter:stable]';

/** One height for skeleton and populated rows: the 24px link target plus the cell padding. */
export const DATA_TABLE_ROW_HEIGHT = 'h-[calc(var(--et-space-7)+2*var(--et-space-4))]';

/**
 * The React keys of a table's skeleton rows: `count` of them, and never fewer than one. A first load passes
 * the table's fixed count; a re-read for another page, sort, search or filter passes the number of rows on
 * screen, so the skeleton rows take the place of the data rows and the table keeps its height.
 */
export function skeletonRowKeys(count: number): readonly string[] {
    const rows = Number.isFinite(count) ? Math.max(1, Math.floor(count)) : 1;
    return Array.from({ length: rows }, (_, row) => `skeleton-row-${row + 1}`);
}

/** A header cell: unshaded, sentence case, the body's size and colour, semibold, with a bottom rule. */
export const DATA_TABLE_HEAD =
    'h-auto border-b border-border bg-card px-(--et-space-4) py-(--et-space-3) text-left align-middle text-[length:var(--et-type-secondary-size)] leading-(--et-type-secondary-line) font-semibold text-foreground';

/*
 * The header row stays at the top of the scroll region while the rows scroll under it, so it stacks
 * above the pinned first-column cells (`--et-z-sticky`); its own first cell, pinned on both axes, stacks
 * above the rest of the header row as the region scrolls sideways.
 *
 * Each header cell also paints the card's fill in a two-border-width strip just above itself. On a
 * scaled display (Windows at 125%, say) the region's clipping edge and the pinned row's top edge round
 * to different device pixels, which left a sliver of the rows scrolling underneath showing above the
 * header; the strip covers it and is otherwise invisible.
 */
const HEADER_COVER = 'shadow-[0_calc(var(--et-border-width)*-2)_0_var(--et-card)]';

/** A header cell that stays at the top of the scroll region. */
export const DATA_TABLE_HEAD_STICKY = `${DATA_TABLE_HEAD} sticky top-0 z-[calc(var(--et-z-sticky)+1)] ${HEADER_COVER}`;

/** The header's first cell when the first column is pinned: sticky on both axes. */
export const DATA_TABLE_HEAD_CORNER = `${DATA_TABLE_HEAD} sticky top-0 ${PINNED_CORNER} z-[calc(var(--et-z-sticky)+2)] ${HEADER_COVER}`;

/** A body cell: secondary type in the body colour, wrapping only between words, with its row's rule and hover fill. */
export const DATA_TABLE_CELL =
    'min-w-24 whitespace-normal border-b border-border px-(--et-space-4) py-(--et-space-4) align-middle text-[length:var(--et-type-secondary-size)] leading-(--et-type-secondary-line) text-foreground group-hover:bg-muted';

/** A pinned first-column body cell's own fill, so the rows scrolling under it stay hidden. */
export const DATA_TABLE_CELL_FILL = 'bg-card';

/** §9 Money and counts: right-aligned in tabular numerals, on one line; give its header the same. */
export const DATA_TABLE_NUMERIC = 'text-right tabular-nums whitespace-nowrap';

/** The ⋯ menu's cell: as narrow as its trigger, at the row's end, with the row's rule and hover fill. */
export const DATA_TABLE_ACTIONS_CELL =
    'w-0 whitespace-nowrap border-b border-border px-(--et-space-2) py-(--et-space-2) text-end align-middle group-hover:bg-muted';
